import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

type Status = 'idle'

// One id for every autosave, so a burst of saves refreshes a single toast instead of stacking.
const TOAST_ID = 'autosave'

/** Tracks an autosave: `track(promise)` shows a short "Saved" toast, or "Couldn't save".
 *  Rejections are swallowed here, so callers can drop their own `.catch`. */
export function useSavedHint() {
  const { t } = useTranslation()
  const track = useCallback(
    (save: Promise<unknown> | void): Promise<boolean> =>
      Promise.resolve(save).then(
        () => {
          toast.success(t('common.savedHint'), { id: TOAST_ID, duration: 1500 })
          return true
        },
        () => {
          toast.error(t('common.saveHintFailed'), { id: TOAST_ID })
          return false
        },
      ),
    [t],
  )
  return { status: 'idle' as Status, track }
}

/** Kept so call sites stay unchanged; the feedback is the toast from `track`. */
export function SavedHint(_: { status: Status; className?: string }) {
  return null
}
